<template>
  <div
    :class="`field-popup ${isActive ? 'field-popup--active' : ''}`"
    :data-id="id"
    :id="`field-popup-${id}`">
    <div class="field-popup__action-message" v-if="!$store.state.isMobile">
      <icon name="exclamation"></icon>
      <span>{{ $t('fieldPopup.tabOrClickToContinue') }}</span>
    </div>
    <h2 class="field-popup__header">
      <span class="field-popup__header__text" v-if="type !== 'dropdown'">{{ title === $t('fieldPopup.untitled') ? $t('fieldPopup.addYourField', { type: $t(`fieldPopup.type.${type}`) }) : title }}</span>
      <span class="field-popup__header__text" v-else>{{ $t('fieldPopup.selectAnOption') }}</span>
      <span v-if="required === 'true'" class="field-popup__header__label">
        <icon name="asterisk" height="10" width="10"></icon>
        <span>{{ $t('fieldPopup.requiredField') }}</span>
      </span>
      <a class="field-popup__close" @click="closeFieldPopup">
        <icon name="close" width="16" height="16"></icon>
      </a>
    </h2>
    <div class="field-popup__content" v-if="type == 'signature'">
      <tabs type="signature">
        <tab :name="$t('fieldPopup.signature.draw')" property="drawn" id="1" iconName="pencil" v-if="config.company_signature_format_drawn === '1'">
          <signature-box
            type="draw"
            :activate-confirm-button="activateConfirmButton"></signature-box>
        </tab>
        <tab :name="$t('fieldPopup.signature.type')" property="typed" id="2" iconName="font" hasInput="true" v-if="config.company_signature_format_typed === '1'">
          <signature-box
            type="type"
            :activate-confirm-button="activateConfirmButton"></signature-box>
        </tab>
        <tab :name="$t('fieldPopup.signature.upload')" property="uploaded" id="3" iconName="cloud-upload" v-if="config.company_signature_format_upload === '1'">
          <fine-uploader
            :activate-confirm-button="activateConfirmButton"
            :field-id="id"
            :options="signatureUploadOpts"
            type="imageUpload"></fine-uploader>
        </tab>
      </tabs>
    </div>
    <div class="field-popup__content" v-if="type == 'text'">
      <input class="field-popup__field-text" v-model="fieldText" @input="updateFieldText" ref='input' />
    </div>
    <div class="field-popup__content" v-if="type == 'textarea'">
      <textarea class="field-popup__field-textarea" v-model="fieldText" @input="updateFieldText" ref='input'></textarea>
    </div>
    <div class="field-popup__content field-popup__content--dropdown" v-if="type == 'dropdown'">
      <div
        v-for="choice in field.dropdown_choices"
        @click.self="selectDropdownChoice"
        :class="{ selected: field.content === choice }">{{ choice }}</div>
    </div>
    <div class="field-popup__content" v-if="type == 'date'">
      <div class="grid">
        <div class="grid__item grid__item--6">
          <datepicker
            v-model="pickedDate"
            :inline="true"
            :field-id="id"
            :language="$i18n.locale"></datepicker>
        </div>
        <div class="grid__item grid__item--6">
          <h1 class="field-popup__date-text" v-if="pickedDate">{{ pickedDate }}</h1>
        </div>
      </div>
    </div>
    <div class="field-popup__content" v-if="type == 'upload'">
      <fine-uploader
        :activate-confirm-button="activateConfirmButton"
        :field-id="id"
        :options="documentUploadOpts"
        type="documentUpload"></fine-uploader>
    </div>
    <div class="field-popup__buttons">
      <app-button
        v-if="type === 'signature' || type == 'upload' || type === 'date'"
        :text="$t('functional.confirm')"
        iconName="check"
        :action="confirm"
        :isActive="isButtonActive"
        classList="button--cta float-right"></app-button>
      <app-button
        v-else
        :text="$store.getters.footerNextButtonText()"
        :action="nextAction"
        classList="button--cta float-right"></app-button>
    </div>
  </div>
</template>

<script>
  import { submitEnvelope } from '@/store/api';
  import SignatureBox from '@/components/SignatureBox';
  import FineUploader from '@/components/FineUploader';
  import Tabs from '@/components/Tabs';
  import Tab from '@/components/Tab';
  import AppButton from '@/components/AppButton';
  import Datepicker from '@/components/DatePicker';

  export default {
    name: 'field-popup',
    props: [
      'type',
      'title',
      'field',
      'id',
      'required',
      'isActive',
      'closeFieldPopup',
      'updateFieldText',
      'selectDropdownChoice',
    ],
    components: {
      SignatureBox,
      FineUploader,
      Tabs,
      Tab,
      Datepicker,
      AppButton,
    },
    data() {
      return {
        config: this.$store.state.config,
        isButtonActive: false,
        pickedDate: null,
        documentUploadOpts: {
          options: {
            request: {
              endpoint: `${window.APP_URL}/ajax/upload-field.php`,
            },
            deleteFile: {
              endpoint: `${window.APP_URL}/ajax/upload-field.php`,
              enabled: true,
              method: 'POST',
            },
            validation: {
              allowedExtensions: ['jpeg', 'jpg', 'png', 'pdf', 'doc', 'docx', 'bmp'],
              acceptFiles: 'image/jpeg, image/png, application/pdf, application/x-pdf, application/msword, application/doc, application/vnd.msword, application/vnd.ms-word, application/winword, application/word, application/x-msw6, application/x-msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/bmp',
              sizeLimit: 5000000,
            },
            multiple: true,
          },
        },
        signatureUploadOpts: {
          options: {
            request: {
              endpoint: `${window.APP_URL}/ajax/upload-signature.php`,
            },
            multiple: false,
            validation: {
              allowedExtensions: ['jpeg', 'jpg', 'png'],
              acceptFiles: 'image/jpeg, image/png',
              sizeLimit: 5000000,
              itemLimit: 1,
            },
          },
        },
      };
    },
    created() {
      this.$bus.$on('confirmField', () => {
        if (parseInt(this.$store.state.activePopupId, 10) === parseInt(this.id, 10)
          && this.isActive && this.isButtonActive) {
          this.confirm();
          this.$bus.$emit('fieldConfirmed');
        }
      });
      this.$bus.$on('calendarSelected', () => {
        this.isButtonActive = parseInt(this.$store.state.activePopupId, 10) === parseInt(this.id, 10);
      });
      this.$bus.$on('disableButton', () => {
        this.isButtonActive = false;
      });
    },
    mounted() {
      this.$el.style.bottom = this.bottomStyle();
    },
    updated() {
      const isTextarea = this.isActive && this.type === 'textarea';
      const isText = this.isActive && this.type === 'text';
      if (isText || isTextarea) {
        this.$refs.input.focus();
        if (this.$refs.input.value === '') {
          this.isButtonActive = false;
        }
      }
      this.$el.style.bottom = this.bottomStyle();
    },
    computed: {
      fieldText: {
        get() {
          const field = this.$store.getters.getField(this.id, true, 'content');
          if (field !== '') {
            this.activateConfirmButton();
          } else {
            this.isButtonActive = false;
          }
          return field;
        },
        set(val) {
          this.$store.dispatch('setCurrentFieldVal', { val, id: this.id, property: 'content' });
          this.activateConfirmButton();
        },
      },
    },
    methods: {
      nextAction() {
        const isEnvelopeDone = this.$store.getters.isEnvelopeComplete();
        const isDocumentDone = this.$store.getters.isDocumentDone();
        const nextField = this.$store.getters.nextField();
        if (isEnvelopeDone) {
          if (window.IS_PREVIEW) {
            alert(this.$t('fieldPopup.signedPreviewWarning'));
          } else {
            this.$bus.$emit('loading');
            submitEnvelope({ store: this.$store })
              .then((response) => {
                this.$bus.$emit('submitEnvelope', { response });
              });
          }
        } else if (isDocumentDone) {
          this.$store.dispatch('nextDocument');
          this.$bus.$emit('nextDocument');
        } else {
          this.$store.commit('setActiveStates', {
            currentPage: nextField.page,
            currentField: nextField.index,
            activePopupId: nextField.id,
          });
          this.$bus.$emit('nextField', { inputId: nextField.id });
          this.$bus.$emit('scrollBody');
          this.$bus.$emit('openedFieldPopup', { field: nextField });
          if (nextField.type === 'checkbox') {
            this.$bus.$emit('highlightField', { field: nextField });
          }
        }
      },
      bottomStyle() {
        let bottom = this.$el.clientHeight + 100;
        if (this.type === 'date') {
          bottom = this.$el.clientHeight + 282 + 100;
        }
        if (!this.isActive) {
          return `-${bottom}px`;
        }
        return '0px';
      },
      confirm() {
        const currentId = String(this.$store.state.activePopupId);
        const currentField = this.$store.getters.getField(currentId, false);
        this.$store.dispatch('addDoneField', { field: currentField });
        this.$store.commit('setActiveStates', { activePopupId: 0 });
        this.$bus.$emit('closeFieldPopup');
        this.$bus.$emit('addedNewDoneField', { field: currentField });
        if (this.type === 'signature') {
          this.$bus.$emit('setSignature', this.id);
        }
        this.$bus.$emit('closedFieldPopup');
      },
      activateConfirmButton() {
        this.isButtonActive = true;
      },
    },
  };
</script>

<style lang="sass" scoped>
  @import '~styles/component-utils.sass'
  $color-popup-shadow: $color-grey
  .field-popup
    background-color: #fff
    position: fixed
    bottom: 0%
    right: 0
    width: 75%
    z-index: 10
    transition: bottom .25s ease-out
    box-shadow: 0px -20px 30px -30px $color-popup-shadow
    cursor: default
    z-index: 999
    opacity: 0
    transition: all .25s ease
    color: #555
    &--active
      opacity: 1
    &__action-message
      position: absolute
      top: -32px
      background: #000
      color: #fff
      padding: 5px 10px
      border-radius: 5px
      transform: translate3d(-50%, 0, 0)
      left: 50%
      font-size: 11px
      font-weight: bold
      text-transform: uppercase
      svg
        height: 12px
        width: 5px
        float: left
    &__header
      padding: $spacing-base
      border-bottom: 1px solid $color-border
      margin: 0
      position: relative
      @include clearfix
      &__text
        float: left
      &__label
        float: left
        font-size: 9px
        padding: $spacing-xs
        background: $color-cancel
        color: #fff
        border: 1px solid $color-cancel-dark
        border-radius: 3px
        margin-left: $spacing-small
        margin-top: $spacing-xs
        text-transform: uppercase
        svg
          margin: 0
          float: left
        span
          float: left
          margin-left: $spacing-xs
    &__content
      padding: $spacing-base
      &--dropdown
        padding: 0
        background: #FAFBFB
        border-bottom: 1px solid #ecf0f1
        margin-bottom: $spacing-base
        box-shadow: inset 0px 0px 7px 0 #eee
        max-height: 165px
        overflow: auto
        > div
          padding: $spacing-base
          font-size: 20px
          &:hover,
          &.selected,
            cursor: pointer
          &:hover
            background: $signable-secondary-1-shade-3
          &.selected
            color: #fff
            background: $signable-secondary-1
    &__buttons
      padding: 0 $spacing-base $spacing-base
      @include clearfix
    &__field-text,
    &__field-textarea,
    &__field-dropdown
      width: 100%
      padding: $spacing-base
      border: 1px solid $color-border-dark
      font-size: $font-size-heading
    &__field-textarea
      height: 250px
    &__field-dropdown
      padding: $spacing-base
      -webkit-appearance: none
      color: inherit
      font-family: inherit
    &__close
      position: absolute
      right: $spacing-base
      top: 12px
      padding: 7px
      background: $color-cancel
      border: 2px solid $color-cancel-dark
      color: #fff
      border-radius: 30px
      line-height: 0
      cursor: pointer
      svg
        margin-right: 0
    &__date-text
      position: absolute
      right: calc(50% - 50%)
      top: 50%
      -ms-top: 0
      transform: translate3d(calc(-50% - (50% - 25%)), -50%, 0)
      -ms-transform: translate3d(-100%, -100%, 0px)
      margin: 0
      padding: $spacing-base
      background-color: $color-blue
      color: #fff
      border-radius: 3px
  // Responsive
  @media screen and (max-width: $screen-size-large)
    .field-popup
      width: 100%
      &__date-text
        position: initial
        right: 0
        transform: none
        text-align: center
  @media screen and (max-width: $screen-size-small)
    .field-popup
      &__date-text
        margin-top: $spacing-small
</style>



// WEBPACK FOOTER //
// FieldPopup.vue?098c2c75